/**
 * @file Stimulus-контроллер каталога.
 * Оркестрирует взаимодействие между состоянием ({@link CatalogState}), моделью ({@link CatalogModel})
 * и видом ({@link CatalogView}): подписывается на изменения состояния и передаёт снимок виду.
 * Также управляет отображением выбранных фильтров, пагинацией и сортировкой.
 */

import { Controller } from '@hotwired/stimulus'
import CatalogView from '../catalog/catalog_view'
import catalogState from '../stores/catalog_state'
import catalogModel from '../stores/catalog_model'
import { createFilterBtn } from '../utils/dom'
import { disableScroll, enableScroll } from '../utils/global'
import { scrollToElement } from '../utils/anchor_scroll'

const CARDS_REQUEST_LIMIT = 48

/**
 * @class
 * @extends Controller
 */
export default class CatalogController extends Controller {
  static targets = [
    'filtersToggle',
    'filtersCount',
    'selectedFilters',
    'list',
    'item',
    'resultCount',
    'pagination',
    'mapLayout',
    'map',
    'mapToggle',
    'mapToggleLabel',
    'mapExpandToggle',
    'emptyState',
    'hideOnEmpty',
    'categoryTab'
  ]

  static values = {
    perPage: { type: Number, default: 12 },
    resetFilterAriaLabel: { type: String, default: 'Reset filter' },
    // Предфильтр из адреса: { filters, sort } от CatalogPrefiltersHelper.
    prefilter: Object,
    // Постраничный каталог (clubs/index.html.slim): в разметке одна страница карточек,
    // данные всей выдачи — sourceUrl, недостающие карточки — cardsUrl, page — страница сервера.
    page: { type: Number, default: 1 },
    sourceUrl: String,
    cardsUrl: String,
    // Адрес первой страницы с параметрами, которые сервер сохраняет в ссылках (ClubsHelper#catalog_page_path).
    baseUrl: String
  }

  #desktopMedia = null
  #mapVisible = false
  #mapExpanded = false
  #scrollLocked = false
  // Номер подключения. Stimulus переподключает тот же экземпляр (перенос узла, морфинг Turbo),
  // и ответ, запрошенный до этого, не должен попасть в модель и вид нового подключения.
  #connection = 0
  #catalogDataLoading = false
  // Номер последнего рендера: ответ на догрузку карточек для устаревшего состояния отбрасывается.
  #renderToken = 0

  /** Инициализация: парсинг элементов, настройка состояния, создание вида, подписка на состояние. */
  connect() {
    // Синхронизировано с --breakpoint-lg в tailwind.css
    this.#desktopMedia = window.matchMedia('(min-width: 1024px)')
    this.breakpointChanged = this.breakpointChanged.bind(this)
    this.#desktopMedia.addEventListener('change', this.breakpointChanged)

    this.#connection += 1

    if (this.#paged) {
      catalogModel.reset()
    } else {
      catalogModel.parseNodesIntoItems(this.itemTargets)
    }

    catalogState.configure({
      pageSize: this.perPageValue,
      page: this.pageValue,
      ...this.prefilterValue
    })
    this.catalogView = new CatalogView({
      root: this.element,
      list: this.hasListTarget ? this.listTarget : null,
      items: this.itemTargets,
      resultCount: this.hasResultCountTarget ? this.resultCountTarget : null,
      pagination: this.hasPaginationTarget ? this.paginationTarget : null,
      emptyState: this.hasEmptyStateTarget ? this.emptyStateTarget : null,
      hideOnEmpty: this.hideOnEmptyTargets
    })

    this.stateChanged = this.stateChanged.bind(this)
    this.unsubscribeFromCatalogState = catalogState.subscribe(this.stateChanged)

    this.stateChanged(catalogState.snapshot)

    const modalFrame = this.#modalFrame()
    if (!modalFrame) return

    this.modalObserver = new MutationObserver(() => this.#syncExpandedState())
    this.modalObserver.observe(modalFrame, { childList: true })
    this.#syncExpandedState()
  }

  /** Отписка от состояния, отключение наблюдателя модального окна, снятие блокировки скролла. */
  disconnect() {
    this.#connection += 1
    this.#catalogDataLoading = false
    this.#desktopMedia?.removeEventListener('change', this.breakpointChanged)
    if (this.#scrollLocked) {
      enableScroll()
      this.#scrollLocked = false
    }
    this.modalObserver?.disconnect()
    this.unsubscribeFromCatalogState?.()
  }

  /**
   * Открывает/закрывает панель фильтров через Turbo Stream в модальном окне.
   */
  toggleFilters() {
    const filtersToggle = this.filtersToggleTarget
    const formPath = filtersToggle.dataset.formPath

    if (
      filtersToggle.getAttribute('aria-expanded') === 'true' &&
      this.#modalHasContent()
    ) {
      filtersToggle.setAttribute('aria-expanded', 'false')
      this.#clearModal()
    } else {
      filtersToggle.setAttribute('aria-expanded', 'true')
      this.#loadTurboStream(formPath)
    }
  }

  /**
   * Переключает видимость карты по состоянию свитча.
   * На мобилке карта раскрывается на весь экран с блокировкой скролла страницы.
   * @param {Event} event
   */
  toggleMap(event) {
    this.#setMapVisible(event.currentTarget.checked)
  }

  /** Расширяет/сворачивает карту внутри контейнера страницы, не включая browser fullscreen. */
  toggleMapExpansion() {
    this.#setMapExpanded(!this.#mapExpanded)
  }

  /** Закрывает полноэкранную карту (кнопка «Show list» на мобилке) и сбрасывает свитч. */
  showList() {
    if (this.hasMapToggleTarget) this.mapToggleTarget.checked = false
    this.#setMapVisible(false)
  }

  /** При переходе через lg-брейкпоинт снимает/возвращает блокировку скролла открытой карты. */
  breakpointChanged(event) {
    // На мобилке карта и так занимает экран; сохранять desktop-разворот при
    // смене брейкпоинта нельзя, иначе список останется скрыт после возврата.
    if (!event.matches) this.#setMapExpanded(false)
    this.#syncScrollLock()
  }

  /**
   * Обработчик изменения состояния: передаёт снимок в `CatalogView` и обновляет выбранные фильтры.
   * @param {{ filters: object, sort: string, page: number, pageSize: number }} state
   */
  stateChanged(state) {
    this.filtersData = state.filters
    this.#updateSelectedFilters()
    this.#syncCategoryTabs()

    // Постраничный каталог до прихода данных: список и ссылки — серверные, их не трогаем.
    // Состояние, которое успели изменить, отрисуется, когда данные придут. Запрос данных здесь же: первый — при подключении,
    // после сбоя — повтор на следующем действии, иначе приглушённый список так и остался бы неактивным.
    if (!catalogModel.ready) {
      if (this.#paged && !this.#catalogDataLoading) this.#loadCatalogData()
      this.#setLoading(!catalogState.isDefaultView(state))
      return
    }

    const snapshot = catalogModel.getSnapshot(state, { now: new Date() })

    if (this.#paged) {
      this.#renderWithCards(snapshot, state)
    } else {
      this.#renderSnapshot(snapshot, state)
    }
  }

  /**
   * Обработчик `data-action` на кнопках сортировки. Достаёт значение `data-sort` и передаёт в состояние.
   * @param {Event} event
   */
  changeSort(event) {
    const option = event.currentTarget.closest('[data-sort]')
    if (!option) return

    catalogState.setSort(option.dataset.sort)
  }

  /**
   * Обработчик кликов по пагинации: определяет нажатую кнопку (назад, вперёд, номер страницы, «показать ещё»)
   * и вызывает соответствующий метод `catalogState`.
   * @param {Event} event
   */
  paginationClick(event) {
    const button = event.target.closest('a, button')
    if (!button || !this.catalogView.containsPaginationButton(button)) return

    // Ссылки страниц — для краулера и новой вкладки. Обычный клик листает на месте,
    // а пока данных каталога нет — переход по ссылке делает сервер.
    if (button.tagName === 'A') {
      const newTab =
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        event.button !== 0
      if (newTab || !catalogModel.ready) return

      event.preventDefault()
    }

    if (button.classList.contains('pagination__show-more')) {
      catalogState.showMore()
      return
    }

    const previousPage = catalogState.snapshot.page

    if (button.classList.contains('pagination__prev')) {
      catalogState.setPage(previousPage - 1)
    } else if (button.classList.contains('pagination__next')) {
      catalogState.setPage(previousPage + 1)
    } else if (button.dataset.page) {
      catalogState.setPage(Number.parseInt(button.dataset.page, 10))
    }

    // Список рендерится на месте, и на неполной странице документ резко
    // укорачивается — браузер обрезает scroll по новой высоте и вьюпорт
    // «падает» к футеру. Возвращаем пользователя к началу каталога.
    if (catalogState.snapshot.page !== previousPage) {
      const catalogSection = this.hasListTarget
        ? this.listTarget.closest('section')
        : null

      scrollToElement(catalogSection ?? this.element)
    }
  }

  /**
   * Удаляет отдельное значение фильтра (убирает тег из строки выбранных фильтров).
   * @param {Event} event
   */
  removeFilter(event) {
    const { filterName, filterValue } = event.currentTarget.dataset

    if (this.filtersData[filterName]) {
      this.filtersData[filterName] = this.filtersData[filterName].filter(
        (v) => v.value !== filterValue
      )
    }

    if (this.filtersData[filterName]?.length === 0) {
      delete this.filtersData[filterName]
    }

    catalogState.setFilters(this.filtersData)

    this.#updateSelectedFilters()
  }

  /**
   * Вкладка категории — одиночный выбор поверх обычного фильтра `categories`:
   * значение вкладки заменяет выбранное, пустое — снимает фильтр («все»).
   * @param {Event} event
   */
  selectCategory(event) {
    const { filterValue, textValue } = event.currentTarget.dataset
    const filters = { ...this.filtersData }

    if (filterValue) {
      filters.categories = [{ value: filterValue, textValue }]
    } else {
      delete filters.categories
    }

    catalogState.setFilters(filters)
  }

  /** Сбрасывает все активные фильтры. */
  resetFilters() {
    this.filtersData = {}
    catalogState.setFilters(this.filtersData)
    this.#updateSelectedFilters()
  }

  get #paged() {
    return this.hasSourceUrlValue && this.sourceUrlValue !== ''
  }

  #renderSnapshot(snapshot, state) {
    this.catalogSnapshot = snapshot
    this.catalogView.render(snapshot, state.sort, this.#pageHref(state))

    // При пустом результате свитч карты скрывается — закрываем открытую карту,
    // иначе её нечем будет выключить
    if (snapshot.totalItems === 0) this.showList()
    this.dispatch('filter', {
      detail: { ids: snapshot.orderedIds },
      bubbles: true
    })

    if (this.#paged) this.#syncPageInAddress(snapshot, state)
  }

  /** Рендер постраничного каталога: сначала догружает карточки, которых нет в списке. */
  async #renderWithCards(snapshot, state) {
    const token = ++this.#renderToken
    const connection = this.#connection
    const stale = () =>
      token !== this.#renderToken || connection !== this.#connection
    const missingIds = snapshot.pageIds.filter(
      (id) => !this.catalogView.hasNode(id)
    )

    if (missingIds.length > 0) {
      this.#setLoading(true)

      try {
        const nodes = await this.#fetchCards(missingIds)
        if (stale()) return

        this.catalogView.addNodes(nodes)
      } catch (error) {
        if (stale()) return

        this.#setLoading(false)
        console.error('Не удалось загрузить карточки каталога:', error)
        return
      }
    }

    if (stale()) return

    this.#setLoading(false)
    this.#renderSnapshot(snapshot, state)
  }

  /** Данные всей выдачи: без них фильтры, сортировка и карта видели бы только одну страницу. */
  async #loadCatalogData() {
    const connection = this.#connection
    this.#catalogDataLoading = true

    try {
      const response = await fetch(this.sourceUrlValue, {
        headers: { Accept: 'application/json' }
      })
      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`)
      }

      const { keys, rows } = await response.json()
      if (connection !== this.#connection) return

      const records = rows.map((row) =>
        Object.fromEntries(keys.map((key, index) => [key, row[index]]))
      )

      catalogModel.parseRecords(records)
      this.dispatch('data', { detail: { records }, bubbles: true })
      this.stateChanged(catalogState.snapshot)
    } catch (error) {
      if (connection !== this.#connection) return

      this.#setLoading(false)
      console.error('Не удалось загрузить данные каталога:', error)
    } finally {
      if (connection === this.#connection) this.#catalogDataLoading = false
    }
  }

  async #fetchCards(ids) {
    const nodes = []

    // CARDS_REQUEST_LIMIT — тот же потолок, что у ClubsController::CATALOG_CARDS_LIMIT.
    for (let start = 0; start < ids.length; start += CARDS_REQUEST_LIMIT) {
      const url = new URL(this.cardsUrlValue, window.location.origin)
      url.searchParams.set(
        'ids',
        ids.slice(start, start + CARDS_REQUEST_LIMIT).join(',')
      )

      const response = await fetch(url, { headers: { Accept: 'text/html' } })
      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`)
      }

      const template = document.createElement('template')
      template.innerHTML = await response.text()
      nodes.push(
        ...template.content.querySelectorAll('[data-catalog-target="item"]')
      )
    }

    return nodes
  }

  /**
   * Номера страниц — ссылки только там, где страница браузера совпадает с серверной:
   * без фильтров, с сортировкой по умолчанию. Иначе ?page=N вёл бы на другой список.
   */
  #pageHref(state) {
    if (!this.#paged || !catalogState.isDefaultView(state)) return null

    return (page) => this.#pagePath(page)
  }

  #pagePath(page) {
    const url = new URL(
      this.baseUrlValue || window.location.pathname,
      window.location.origin
    )
    if (page > 1) url.searchParams.set('page', page)

    return `${url.pathname}${url.search}`
  }

  /**
   * Адрес повторяет открытую страницу списка: обновление и ссылка ведут туда же. replaceState,
   * а не pushState: история остаётся за Turbo, «Назад» уводит со страницы каталога.
   */
  #syncPageInAddress(snapshot, state) {
    const url = new URL(window.location.href)

    if (catalogState.isDefaultView(state) && snapshot.page > 1) {
      url.searchParams.set('page', snapshot.page)
    } else {
      url.searchParams.delete('page')
    }

    if (url.href !== window.location.href) {
      window.history.replaceState(window.history.state, '', url)
    }
  }

  /** Список приглушается, пока в нём не то, что выбрано (ждём данные или карточки). */
  #setLoading(loading) {
    if (!this.hasListTarget) return

    this.listTarget.setAttribute('aria-busy', String(loading))
  }

  /**
   * Показывает/скрывает контейнер карты и синхронизирует блокировку скролла.
   * @param {boolean} visible
   */
  #setMapVisible(visible) {
    if (!this.hasMapTarget) return
    this.#mapVisible = visible
    this.mapTarget.classList.toggle('hidden', !visible)
    this.#syncMapToggleLabel(visible)
    if (!visible) this.#setMapExpanded(false)
    this.#syncScrollLock()
  }

  /**
   * Свитч и открывает, и закрывает карту, поэтому подпись должна называть
   * действие, доступное сейчас: «Показать карту» → «Скрыть карту».
   * @param {boolean} visible
   */
  #syncMapToggleLabel(visible) {
    if (!this.hasMapToggleLabelTarget) return

    const label = this.mapToggleLabelTarget
    const text = visible ? label.dataset.hideLabel : label.dataset.showLabel
    if (text) label.textContent = text
  }

  /**
   * Меняет ширину карты только в сетке каталога и синхронизирует a11y-состояние.
   * @param {boolean} expanded
   */
  #setMapExpanded(expanded) {
    if (!this.hasMapLayoutTarget || !this.hasMapExpandToggleTarget) return

    this.#mapExpanded = expanded
    this.mapLayoutTarget.classList.toggle(
      'catalog-map-layout--expanded',
      expanded
    )

    const toggle = this.mapExpandToggleTarget
    toggle.setAttribute('aria-expanded', String(expanded))
    toggle.setAttribute(
      'aria-label',
      expanded ? toggle.dataset.collapseLabel : toggle.dataset.expandLabel
    )
  }

  /** Скролл страницы блокируется только пока карта открыта на мобилке (fullscreen-оверлей). */
  #syncScrollLock() {
    const shouldLock = this.#mapVisible && !this.#desktopMedia.matches
    if (shouldLock === this.#scrollLocked) return

    this.#scrollLocked = shouldLock
    if (shouldLock) {
      disableScroll()
    } else {
      enableScroll()
    }
  }

  async #loadTurboStream(url) {
    try {
      const response = await fetch(url, {
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      if (!response.ok) {
        throw new Error(
          `Failed to load turbo stream: ${response.status} ${response.statusText}`
        )
      }
      const html = await response.text()
      Turbo.renderStreamMessage(html)
    } catch (error) {
      console.error('Ошибка загрузки turbo stream:', error)
    }
  }

  #clearModal() {
    const modalFrame = this.#modalFrame()
    if (modalFrame) modalFrame.innerHTML = ''
  }

  #modalFrame() {
    return document.getElementById('modal')
  }

  #modalHasContent() {
    return this.#modalFrame()?.children.length > 0
  }

  #syncExpandedState() {
    if (!this.hasFiltersToggleTarget) return
    if (!this.#modalHasContent()) {
      this.filtersToggleTarget.setAttribute('aria-expanded', 'false')
    }
  }

  /**
   * Активная вкладка отмечается по состоянию, а не по клику: фильтр может
   * измениться и мимо вкладок (сброс, чипы выбранных фильтров).
   */
  #syncCategoryTabs() {
    if (this.categoryTabTargets.length === 0) return

    const selected = this.filtersData?.categories?.[0]?.value ?? ''

    this.categoryTabTargets.forEach((tab) => {
      const isSelected = (tab.dataset.filterValue || '') === selected
      tab.setAttribute('aria-pressed', String(isSelected))
    })
  }

  /** Обновляет бейдж со счётчиком активных фильтров на кнопке-переключателе фильтров. */
  #updateFiltersCount() {
    if (!this.hasFiltersCountTarget) return

    const count = Object.values(this.filtersData || {}).reduce(
      (sum, values) => sum + values.length,
      0
    )

    this.filtersCountTarget.textContent = count > 0 ? count : ''
    this.filtersCountTarget.classList.toggle(
      'filter-toggle__badge--hidden',
      count === 0
    )
  }

  #updateSelectedFilters() {
    this.#updateFiltersCount()

    if (!this.hasSelectedFiltersTarget) return

    const selectedFilters = this.selectedFiltersTarget
    const btnsToRemove = selectedFilters.querySelectorAll('.filter-btn')
    btnsToRemove.forEach((btn) => btn.remove())

    Object.entries(this.filtersData).forEach(([name, values]) => {
      values.forEach((value) => {
        const filterBtn = createFilterBtn(value.textValue, false, true, {
          'data-action': 'click->catalog#removeFilter',
          'data-filter-name': name,
          'data-filter-value': value.value,
          'aria-label': this.resetFilterAriaLabelValue
        })
        selectedFilters.appendChild(filterBtn)
      })
    })

    this.selectedFiltersTarget.classList.toggle(
      'hidden',
      // eslint-disable-next-line no-undefined
      this.filtersData === undefined ||
        Object.keys(this.filtersData).length === 0
    )
  }
}
